"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, usePresence } from "framer-motion";

// Hook for managing debounced values
export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Animated item wrapper with smooth entry/exit
export function AnimatedGridItem({
  children,
  layoutId,
  className = "",
  delay = 0,
  ...props
}) {
  const [isPresent, safeToRemove] = usePresence();

  useEffect(() => {
    !isPresent && setTimeout(safeToRemove, 300);
  }, [isPresent, safeToRemove]);

  return (
    <motion.div
      layout
      layoutId={layoutId}
      className={className}
      initial={{
        opacity: 0,
        scale: 0.8,
        y: 20,
      }}
      animate={{
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
          duration: 0.4,
          delay: delay * 0.05,
          ease: [0.4, 0.0, 0.2, 1],
        },
      }}
      exit={{
        opacity: 0,
        scale: 0.8,
        y: -20,
        transition: {
          duration: 0.3,
          ease: [0.4, 0.0, 0.2, 1],
        },
      }}
      whileHover={{
        scale: 1.02,
        transition: { duration: 0.2 },
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// Grid container with stagger animations
export function AnimatedGrid({
  children,
  className = "",
  staggerDelay = 0.05,
  ...props
}) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: staggerDelay,
            delayChildren: 0.1,
          },
        },
      }}
      {...props}
    >
      <AnimatePresence mode="popLayout">{children}</AnimatePresence>
    </motion.div>
  );
}

// Search result wrapper with typing indicator
export function SearchWrapper({
  children,
  isSearching = false,
  hasResults = true,
  searchQuery = "",
  emptyMessage = "No results found",
  className = "",
}) {
  return (
    <div className={className}>
      <AnimatePresence mode="wait">
        {isSearching ? (
          <motion.div
            key="searching"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center justify-center py-8"
          >
            <div className="flex items-center space-x-3">
              <div className="flex space-x-1">
                <motion.div
                  className="w-2 h-2 bg-accent-lime rounded-full"
                  animate={{ y: [-4, 0, -4] }}
                  transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                />
                <motion.div
                  className="w-2 h-2 bg-accent-lime rounded-full"
                  animate={{ y: [-4, 0, -4] }}
                  transition={{ duration: 0.6, repeat: Infinity, delay: 0.1 }}
                />
                <motion.div
                  className="w-2 h-2 bg-accent-lime rounded-full"
                  animate={{ y: [-4, 0, -4] }}
                  transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                />
              </div>
              <span className="text-neutral-light/70 text-sm">
                Searching{searchQuery && ` for "${searchQuery}"`}...
              </span>
            </div>
          </motion.div>
        ) : !hasResults ? (
          <motion.div
            key="no-results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center py-12"
          >
            <div className="text-6xl mb-4 opacity-20">🔍</div>
            <h3 className="text-xl font-semibold text-neutral-light mb-2">
              {emptyMessage}
            </h3>
            {searchQuery && (
              <p className="text-neutral-light/70">
                Try adjusting your search terms or filters
              </p>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// FAQ Section with smooth accordion animation
export function AnimatedAccordion({ isOpen, children, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={false}
      animate={{
        height: isOpen ? "auto" : 0,
        opacity: isOpen ? 1 : 0,
      }}
      transition={{
        duration: 0.3,
        ease: [0.4, 0.0, 0.2, 1],
      }}
      style={{ overflow: "hidden" }}
    >
      <motion.div
        initial={false}
        animate={{
          y: isOpen ? 0 : -20,
        }}
        transition={{
          duration: 0.3,
          ease: [0.4, 0.0, 0.2, 1],
        }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
